import React, { useState } from 'react';
import { jsPDF } from "jspdf";
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToPdfProps {}

// PDF orientation options
type PdfOrientation = 'portrait' | 'landscape';

// PDF page size options
type PdfPageSize = 'a4' | 'letter' | 'legal';

const JsonToPdf: React.FC<JsonToPdfProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for page orientation
  const [orientation, setOrientation] = useState<PdfOrientation>('portrait');
  // State for page size
  const [pageSize, setPageSize] = useState<PdfPageSize>('a4');
  // State for font size
  const [fontSize, setFontSize] = useState<number>(10);
  // State to track if the conversion happened
  const [isPdfGenerated, setIsPdfGenerated] = useState<boolean>(false);
  // State for output format (PDF or JSON)
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'json'>('pdf');

  

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsPdfGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsPdfGenerated(false);
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setError('');
    setFileName('json_data');
    setOrientation('portrait');
    setPageSize('a4');
    setFontSize(10);
    setOutputFormat('pdf');
    setIsPdfGenerated(false);
  };

  // Convert JSON to PDF
  const convertJsonToPdf = () => {
    setError('');
    setIsLoading(true);
    setIsPdfGenerated(false);
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      if (outputFormat === 'pdf') {
        // Create new jsPDF instance with specified options
        const doc = new jsPDF({
          orientation: orientation,
          unit: 'mm',
          format: pageSize
        });
        
        // Format the JSON data with proper indentation
        const formattedJson = JSON.stringify(jsonData, null, 2);
        
        // Split the formatted JSON string into lines
        const lines = formattedJson.split('\n');
        
        // PDF document settings
        const lineHeight = fontSize * 0.35;
        const margin = 10;
        let y = margin;
        
        // Set font
        doc.setFontSize(fontSize);
        doc.setFont('courier');
        
        // Add title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fontSize + 6);
        doc.text('JSON Data', margin, y);
        y += lineHeight * 2;
        
        // Add timestamp
        doc.setFontSize(fontSize - 2);
        doc.setFont('helvetica', 'italic');
        const timestamp = new Date().toLocaleString();
        doc.text(`Generated: ${timestamp}`, margin, y);
        y += lineHeight * 2;
        
        // Reset font for content
        doc.setFont('courier', 'normal');
        doc.setFontSize(fontSize);
        
        // Add each line of JSON to the PDF
        lines.forEach(line => {
          // Check if we need a new page
          if (y > doc.internal.pageSize.height - margin) {
            doc.addPage();
            y = margin;
          }
          
          doc.text(line, margin, y);
          y += lineHeight;
        });
        
        // Save the PDF
        doc.save(`${fileName || 'json_data'}.pdf`);
        
        setIsPdfGenerated(true);
        setIsLoading(false);
      } else {
        // JSON download logic
        const formattedJson = JSON.stringify(jsonData, null, 2);
        const blob = new Blob([formattedJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        link.href = url;
        link.download = `${fileName || 'json_data'}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setIsPdfGenerated(true);
        setIsLoading(false);
      }
    } catch (e) {
      setError(`Error generating file: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to PDF Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to PDF documents or download as formatted JSON files. Paste your JSON data below to generate a well-formatted PDF document.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="outputFormat" className="block text-sm font-medium text-gray-700 mb-1">
                Output Format
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="pdf"
                    checked={outputFormat === 'pdf'}
                    onChange={() => setOutputFormat('pdf')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="pdf" className="ml-2 text-gray-700">PDF</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="json"
                    checked={outputFormat === 'json'}
                    onChange={() => setOutputFormat('json')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="json" className="ml-2 text-gray-700">JSON</label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && outputFormat === 'pdf' && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="pageSize" className="block text-sm font-medium text-gray-700 mb-1">
                    Page Size
                  </label>
                  <select
                    id="pageSize"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={pageSize}
                    onChange={(e) => setPageSize(e.target.value as PdfPageSize)}
                  >
                    <option value="a4">A4</option>
                    <option value="letter">Letter</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="orientation" className="block text-sm font-medium text-gray-700 mb-1">
                    Orientation
                  </label>
                  <select
                    id="orientation"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value as PdfOrientation)}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 mb-1">
                    Font Size
                  </label>
                  <input
                    type="number"
                    id="fontSize"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={fontSize}
                    onChange={(e) => setFontSize(Math.max(6, Math.min(16, parseInt(e.target.value) || 10)))}
                    min="6"
                    max="16"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 8-12 for better readability
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToPdf}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  `Convert to ${outputFormat.toUpperCase()}`
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Preview Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Preview &amp; Status
            </label>
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isPdfGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    {outputFormat === 'pdf' 
                      ? "Your PDF document will be generated and downloaded when you click the convert button." 
                      : "Your formatted JSON file will be downloaded when you click the convert button."}
                  </p>
                </div>
              </div>
            )}
            
            {!error && isPdfGenerated && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md flex-grow">
                <div className="flex items-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">{outputFormat === 'pdf' ? 'PDF' : 'JSON'} Successfully Generated!</p>
                </div>
                
                <p className="text-sm mb-2">
                  Your file has been downloaded as: <span className="font-semibold">{fileName}.{outputFormat}</span>
                </p>
                
                {outputFormat === 'pdf' && (
                  <>
                    <p className="text-sm mb-2">Document settings:</p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Page size: {pageSize.toUpperCase()}</li>
                      <li>Orientation: {orientation}</li>
                      <li>Font size: {fontSize}pt</li>
                    </ul>
                  </>
                )}
                
                <button
                  className="mt-4 px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 text-sm"
                  onClick={convertJsonToPdf}
                >
                  Download Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to PDF Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool takes your JSON data and converts it into a well-formatted PDF document or JSON file.
              It preserves the structure and indentation of your JSON data, making it easy to read and share.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>PDF Generation</strong> - Creates a clean, readable PDF from your JSON data</li>
              <li><strong>JSON Formatting</strong> - Properly formats and indents your JSON data</li>
              <li><strong>Customizable Options</strong> - Adjust page size, orientation, and font size</li>
              <li><strong>Error Validation</strong> - Ensures your JSON is valid before processing</li>
              <li><strong>Sample Data</strong> - Includes example data to demonstrate the tool's capabilities</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data</li>
              <li>For large JSON datasets, consider using landscape orientation and smaller font size</li>
              <li>Give your file a descriptive name that reflects its content</li>
              <li>If your JSON contains sensitive information, remember that PDF files can be read by anyone with access to the file</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToPdf;